import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import Modal from '@components/Modal';
import XCircle from '@components/Icons/XCircle';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { useToast, useUser } from '@/hooks';
import { ToastTypeVariants } from '@/context/ToastContext';
import { Input } from '@/components/DesignLibrary/Atoms';
import DocumentUploader, { DocumentUploaderRef } from '@/components/Document/DocumentUploader';
import { nanoid } from 'nanoid';
import Upload from '@components/Icons/Upload';
import Button from '@/components/DesignLibrary/Button';
import { documents } from '@/tsClient';
import { addBankAccountDocuments } from '@/tsClient/bankAccountIntegration';
import CheckCircle from '@/components/Icons/CheckCircle';
import ArrowCircleLeft from '@components/Icons/ArrowCircleLeft';
import RadioButton from '@components/Icons/RadioButton';
import Circle from '@components/Icons/Circle';
import Buttons from '@/components/Buttons';
import BannerStatus from '@/components/DesignLibrary/BannerStatus';

import Bluebird from 'bluebird';
import s from './UploadBankStatementModal.module.scss';
import SizeErrorModal from './SizeErrorModal';

export type ModalConfig = {
  step?: 'choose' | 'manual';
  fieldValues?: {
    institutionName?: string;
    accountName?: string;
  };
  disabledFields?: Array<'INSTITUTION_NAME' | 'ACCOUNT_NAME'>;
};

type Props = {
  bankAccountIntegrationId: string;
  creatorId: string;
  onClose: () => void;
  getBankStatementOrganizerRequest: ({ bankAccountIntegrationId }: { bankAccountIntegrationId: string }) => unknown;
  config?: ModalConfig;
};

type UploadedDocument = Awaited<ReturnType<typeof documents.getDocumentByUploadId>>;

const UploadBankStatementsModal = ({
  bankAccountIntegrationId,
  creatorId,
  getBankStatementOrganizerRequest,
  onClose,
  config,
}: Props): ReactElement => {
  const { getData: getCreateLinkToken, data: createLinkToken } = useNoodleApi(tsClient.plaid.getCreateLinkToken);
  const { getData: getExchangePublicToken } = useNoodleApi(tsClient.plaid.exchangePublicToken);
  const [institutionName, setInstitutionName] = useState(config?.fieldValues?.institutionName ?? '');
  const [accountName, setAccountName] = useState(config?.fieldValues?.accountName ?? '');
  const [uploadError, setUploadError] = useState<Error | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const documentUploaderRef = useRef<DocumentUploaderRef>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [user] = useUser();
  const addToast = useToast();
  const [step, setStep] = useState<'choose' | 'manual' | 'plaid'>(config?.step ?? 'choose');
  const [uploadType, setUploadType] = useState<'plaid' | 'manual'>(config?.step === 'manual' ? 'manual' : 'plaid');
  const [isModalBusy, setIsModalBusy] = useState<boolean>(false);
  const [isSizeErrorModalOpen, setIsSizeErrorModalOpen] = useState<boolean>(false);

  const { getData: addBankAccountDocumentsFn } = useNoodleApi(addBankAccountDocuments, { toastOnError: true });

  const handleFinishUpload = async (uploadedDocs: UploadedDocument[]): Promise<void> => {
    if (uploadedDocs && uploadedDocs.length) {
      await Bluebird.mapSeries(uploadedDocs, async uploadedDoc => {
        await addBankAccountDocumentsFn({
          accountName,
          accountSubType: accountName,
          bankAccountIntegrationId,
          documentId: uploadedDoc?.id,
          documentName: uploadedDoc?.fileName,
          institutionName,
          type: 'manual',
        });
      });
    }
    getBankStatementOrganizerRequest({ bankAccountIntegrationId });
    onClose();
    setIsUploading(false);
  };

  useEffect(() => {
    if (user?.id) {
      getCreateLinkToken({ userId: user.id });
    }
  }, [getCreateLinkToken, user?.id]);

  const { open, ready } = usePlaidLink({
    onExit: err => {
      if (err) {
        setError('Failed to connect with Plaid');
      }
      setIsModalBusy(false);
    },
    onSuccess: async (publicToken, metadata) => {
      if (!publicToken) {
        setError('No public token received');
        setIsModalBusy(false);
        return;
      }
      await getExchangePublicToken({ bankAccountIntegrationId, metaData: metadata, publicToken, userId: user?.id || '' });
      addToast(ToastTypeVariants.SUCCESS, 'Bank statements uploaded successfully');
      getBankStatementOrganizerRequest({ bankAccountIntegrationId });
      onClose();
      setIsModalBusy(false);
    },
    token: createLinkToken?.linkToken || null,
  });

  // UI for the first step: choose upload type
  const renderChooseType = (): ReactElement => (
    <div className={s.container}>
      <h1 className={s.chooseTitle}>How do you want to provide Bank Statements?</h1>
      <Buttons
        isWrapper
        className={uploadType === 'plaid' ? `${s.uploadOption} ${s['uploadOption--selected']}` : s.uploadOption}
        onClick={() => setUploadType('plaid')}
      >
        {uploadType === 'plaid' ? <RadioButton weight="fill" size={16} /> : <Circle size={16} />}
        <div>
          <p className={s.uploadOptionTitle}>
            Connect with Plaid <span className={s.recommendedBadge}>Recommended</span>
          </p>
          <p className={s.uploadOptionDescription}>Securely connect your bank accounts automatically retrieve your bank statements</p>
          <ul className={s.uploadOptionList}>
            <li>
              <CheckCircle weight="fill" size={16} /> Fast and easy set up
            </li>
            <li>
              <CheckCircle weight="fill" size={16} /> Secure, read-only access
            </li>
            <li>
              <CheckCircle weight="fill" size={16} /> Automatically organizes your statements
            </li>
          </ul>
        </div>
      </Buttons>
      <Buttons
        isWrapper
        className={uploadType === 'manual' ? `${s.uploadOption} ${s['uploadOption--selected']}` : s.uploadOption}
        onClick={() => setUploadType('manual')}
      >
        {uploadType === 'manual' ? <RadioButton weight="fill" size={16} /> : <Circle size={16} />}
        <div>
          <div className={s.uploadOptionTitle}>Upload manually</div>
          <div className={s.uploadOptionDescription}>Manually upload bank statements from your computer</div>
          <ul className={s.uploadOptionList}>
            <li>
              <CheckCircle weight="fill" size={16} /> Upload PDF or image files
            </li>
            <li>
              <CheckCircle weight="fill" size={16} /> Works with any bank
            </li>
            <li>
              <CheckCircle weight="fill" size={16} /> No online banking credentials required
            </li>
          </ul>
        </div>
      </Buttons>

      <Button
        variant="primary"
        fullWidth
        size="md"
        onClick={() => {
          if (uploadType === 'plaid') {
            if (ready) {
              setIsModalBusy(true);
              open();
            }
          } else {
            setStep('manual');
          }
        }}
      >
        Next
      </Button>
    </div>
  );

  // UI for manual upload
  const renderManualUpload = (): ReactElement => (
    <div className={s.container}>
      <Input
        value={institutionName}
        disabled={config?.disabledFields?.includes('INSTITUTION_NAME')}
        onChange={val => setInstitutionName(val)}
        label="Institution Name"
        compact
      />
      <Input
        value={accountName}
        disabled={config?.disabledFields?.includes('ACCOUNT_NAME')}
        onChange={val => setAccountName(val)}
        label="Account Name"
        compact
      />

      <div className={s.fileSelectionSection}>
        <Upload size={24} weight="fill" />
        <p className="caption">Upload your bank statements<br/>in PDF or JPEG format</p>
        <Button
          variant="secondary"
          size="sm"
          loading={isUploading}
          disabled={!institutionName || !accountName}
          onClick={() => documentUploaderRef.current?.uploadDocument()}>
          Select files
        </Button>
      </div>
      <DocumentUploader
        id={nanoid()}
        ref={documentUploaderRef}
        creatorId={creatorId}
        onUploadProgressChange={() => null}
        onUploadError={setUploadError}
        onFinishUpload={handleFinishUpload}
        onUploadStart={() => setIsUploading(true)}
        isMultiple={true}
        checkSize={true}
        isSizeError={setIsSizeErrorModalOpen}
      />
    </div>
  );

  return (
    <>
      {!isModalBusy && <Modal
        hideBottomInset
        title="Upload Bank Statements"
        closeIcon={
          step === 'manual' ? <ArrowCircleLeft size={24} weight="fill" /> : <XCircle weight="fill" size={24} color="var(--color-gray-100)" />
        }
        onClose={step === 'manual' && config?.step !== 'manual' ? () => setStep('choose') : onClose}
      >
        {(error || uploadError) && <div className={s.error}><BannerStatus status='error' label="Failed to add account" /></div>}
        <>
          {step === 'choose' && renderChooseType()}
          {step === 'manual' && renderManualUpload()}
        </>
      </Modal>}
      {isSizeErrorModalOpen && <SizeErrorModal setInvalidDocModalOpen={setIsSizeErrorModalOpen} />}
    </>
  );
};

export default UploadBankStatementsModal;
